const STORAGE_KEY = 'salee-search-overlay-recent-terms';
const MAX_RECENT_TERMS = 6;

/**
 * Turns a user-entered query into a tidy, safe value for the recent-search list.
 * @param {unknown} value
 * @returns {string}
 */
function normalizeTerm(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

/**
 * Gets this visitor's recently searched terms. The list deliberately stays in their browser.
 * @returns {string[]}
 */
function getRecentTerms() {
  try {
    const savedTerms = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(savedTerms)) return [];

    return savedTerms
      .map(normalizeTerm)
      .filter(Boolean)
      .filter((term, index, terms) => terms.findIndex((item) => item.toLowerCase() === term.toLowerCase()) === index)
      .slice(0, MAX_RECENT_TERMS);
  } catch {
    return [];
  }
}

/**
 * Adds a query to this visitor's recent-search list.
 * @param {unknown} value
 */
function rememberSearch(value) {
  const term = normalizeTerm(value);
  if (!term) return;

  try {
    const recentTerms = getRecentTerms().filter((item) => item.toLowerCase() !== term.toLowerCase());
    localStorage.setItem(STORAGE_KEY, JSON.stringify([term, ...recentTerms].slice(0, MAX_RECENT_TERMS)));
  } catch {
    // Search still works when the browser blocks local storage.
  }
}

/**
 * Rebuilds the visitor-specific search chips after the empty state is rendered.
 * @param {ParentNode} [root]
 */
function renderRecentSearches(root = document) {
  const recentTerms = getRecentTerms();

  root.querySelectorAll('[data-salee-search-overlay]').forEach((overlay) => {
    if (!(overlay instanceof HTMLElement)) return;

    const mode = overlay.dataset.saleeSearchMode || 'manual_and_recent';
    const manualSearches = overlay.querySelector('[data-salee-popular-searches]');
    const recentSearches = overlay.querySelector('[data-salee-recent-searches]');
    const recentSearchChips = overlay.querySelector('[data-salee-recent-search-chips]');

    if (!(manualSearches instanceof HTMLElement) || !(recentSearches instanceof HTMLElement) || !(recentSearchChips instanceof HTMLElement)) {
      return;
    }

    recentSearchChips.replaceChildren();

    if (mode === 'manual' || recentTerms.length === 0) {
      manualSearches.hidden = false;
      recentSearches.hidden = true;
      return;
    }

    recentTerms.forEach((term) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'salee-search-overlay__chip';
      chip.dataset.saleeSearchTerm = term;
      chip.textContent = term;
      recentSearchChips.append(chip);
    });

    recentSearches.hidden = false;
    manualSearches.hidden = mode !== 'manual_and_recent';
  });
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('predictive-search-form')) return;

  const searchInput = form.querySelector('input[name="q"]');
  if (searchInput instanceof HTMLInputElement) {
    rememberSearch(searchInput.value);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const searchChip = target.closest('[data-salee-search-term]');
  if (!(searchChip instanceof HTMLButtonElement)) return;

  const searchTerm = searchChip.dataset.saleeSearchTerm;
  if (!searchTerm) return;

  const searchComponent = searchChip.closest('.search-modal')?.querySelector('predictive-search-component');
  if (!(searchComponent instanceof HTMLElement) || typeof searchComponent.searchSuggestedTerm !== 'function') return;

  rememberSearch(searchTerm);
  searchComponent.searchSuggestedTerm(searchTerm);
});

document.addEventListener('salee:search-overlay-refresh', () => {
  renderRecentSearches();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => renderRecentSearches(), { once: true });
} else {
  renderRecentSearches();
}
